import React from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Lightbulb as TipIcon,
  Star as ImportantIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

type CalloutVariant = 'info' | 'warning' | 'error' | 'success' | 'tip' | 'important' | 'neutral';

interface CalloutProps {
  variant?: CalloutVariant;
  title?: string;
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  elevation?: number;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const variantConfig = {
  info: {
    icon: <InfoIcon />,
    color: '#0288d1',
    bgColor: '#e1f5fe',
    borderColor: '#0288d1',
  },
  warning: {
    icon: <WarningIcon />,
    color: '#f57c00',
    bgColor: '#fff3e0',
    borderColor: '#f57c00',
  },
  error: {
    icon: <ErrorIcon />,
    color: '#d32f2f',
    bgColor: '#ffebee',
    borderColor: '#d32f2f',
  },
  success: {
    icon: <SuccessIcon />,
    color: '#388e3c',
    bgColor: '#e8f5e9',
    borderColor: '#388e3c',
  },
  tip: {
    icon: <TipIcon />,
    color: '#7c4dff',
    bgColor: '#f3e5f5',
    borderColor: '#7c4dff',
  },
  important: {
    icon: <ImportantIcon />,
    color: '#ffc107',
    bgColor: '#fffde7',
    borderColor: '#ffc107',
  },
  neutral: {
    icon: null,
    color: '#616161',
    bgColor: '#fafafa',
    borderColor: '#bdbdbd',
  },
};

/**
 * Callout component for highlighting important information
 * Supports various variants for different types of messages
 */
export const Callout: React.FC<CalloutProps> = ({
  variant = 'info',
  title,
  icon,
  dismissible = false,
  onDismiss,
  elevation = 0,
  fullWidth = true,
  children,
}) => {
  const theme = useTheme();
  const [dismissed, setDismissed] = React.useState(false);
  const config = variantConfig[variant];

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (dismissed) {
    return null;
  }

  const displayIcon = icon !== undefined ? icon : config.icon;

  return (
    <Paper
      elevation={elevation}
      sx={{
        p: 2,
        mb: 2,
        width: fullWidth ? '100%' : 'auto',
        backgroundColor: alpha(config.bgColor, 0.5),
        borderLeft: `4px solid ${config.borderColor}`,
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {displayIcon && (
          <Box
            sx={{
              mr: 2,
              color: config.color,
              display: 'flex',
              alignItems: 'center',
              mt: 0.5,
            }}
          >
            {displayIcon}
          </Box>
        )}

        <Box sx={{ flex: 1 }}>
          {title && (
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
          )}
          <Box
            sx={{
              color: theme.palette.text.secondary,
              '& > *': {
                margin: 0,
              },
              '& p': {
                marginBottom: theme.spacing(1),
                '&:last-child': {
                  marginBottom: 0,
                },
              },
              '& ul, & ol': {
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1),
                paddingLeft: theme.spacing(3),
              },
              '& code': {
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                padding: '2px 4px',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875em',
              },
            }}
          >
            {children}
          </Box>
        </Box>

        {dismissible && (
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              ml: 1,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Paper>
  );
};

/**
 * Specialized callout components for common use cases
 */
export const InfoCallout: React.FC<Omit<CalloutProps, 'variant'>> = (props) => (
  <Callout variant="info" {...props} />
);

export const WarningCallout: React.FC<Omit<CalloutProps, 'variant'>> = (props) => (
  <Callout variant="warning" {...props} />
);

export const ErrorCallout: React.FC<Omit<CalloutProps, 'variant'>> = (props) => (
  <Callout variant="error" {...props} />
);

export const SuccessCallout: React.FC<Omit<CalloutProps, 'variant'>> = (props) => (
  <Callout variant="success" {...props} />
);

export const TipCallout: React.FC<Omit<CalloutProps, 'variant'>> = (props) => (
  <Callout variant="tip" {...props} />
);

export const ImportantCallout: React.FC<Omit<CalloutProps, 'variant'>> = (props) => (
  <Callout variant="important" {...props} />
);

export default Callout;